import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef } from "react";
import { isSupportedAudioPitch } from "./audioPitch";

const samples = [[23, "b0"], [28, "e1"], [40, "e2"], [45, "a2"], [50, "d3"],
  [55, "g3"], [60, "c4"], [65, "f4"], [70, "bf4"], [75, "ef5"], [80, "af5"]];
export const AUDIO_RESUME_TIMEOUT_MS = 5000;

// One mounted resource owner. Imperative play commands are events, including
// repeated identical pitches; loading/state renders never replay an old command.
const NotePlayer = forwardRef(function NotePlayer({ onAudioStateChange }, ref) {
  const current = useRef(null);
  const notify = useRef(onAudioStateChange);
  useLayoutEffect(() => { notify.current = onAudioStateChange; }, [onAudioStateChange]);
  const report = useCallback((status, message) => notify.current?.({ status, message }), []);

  const silence = useCallback(resource => {
    for (const node of resource.sources) {
      node.source.onended = null;
      try { node.source.stop(); } catch { /* Already ended. */ }
      node.source.disconnect(); node.gain.disconnect();
    }
    resource.sources.clear();
  }, []);
  const release = useCallback(resource => {
    if (!resource) return;
    resource.controller.abort();
    clearTimeout(resource.activationTimer);
    resource.cancelActivation?.();
    if (resource.context) resource.context.onstatechange = null;
    silence(resource);
    if (resource.context && resource.context.state !== "closed") {
      Promise.resolve(resource.context.close()).catch(() => {});
    }
  }, [silence]);
  const fail = useCallback((resource, message) => {
    if (current.current !== resource) return;
    current.current = null;
    release(resource);
    report("error", message);
  }, [release, report]);

  const prepare = useCallback(() => {
    release(current.current);
    const resource = { controller: new AbortController(), sources: new Set(), buffers: null,
      context: null, activated: false, activation: null };
    current.current = resource;
    report("loading", "Preparing Ear Trainer audio…");
    try {
      const Context = window.AudioContext || window.webkitAudioContext;
      resource.context = new Context();
    } catch {
      fail(resource, "Audio is unavailable in this browser. Retry audio or try another browser.");
      return;
    }
    const context = resource.context;
    context.onstatechange = () => {
      if (current.current !== resource || !resource.buffers) return;
      if (context.state === "closed") {
        fail(resource, "Audio has closed. Retry audio before starting training again.");
      } else if (context.state === "running") report("ready");
      else if (resource.activated) {
        silence(resource);
        report("blocked", "Sound was paused by your browser. Resume audio, then start training again.");
      }
    };
    Promise.all(samples.map(async ([, name]) => {
      const response = await fetch(`/samples/${name}.mp3`, { signal: resource.controller.signal });
      if (!response.ok) throw new Error(`Sample HTTP ${response.status}`);
      const data = await response.arrayBuffer();
      if (current.current !== resource) return null;
      return context.decodeAudioData(data);
    })).then(buffers => {
      if (current.current !== resource || resource.controller.signal.aborted) return;
      resource.buffers = buffers;
      report("ready"); // Prepared, possibly suspended until a user gesture.
    }).catch(() => {
      if (current.current === resource && !resource.controller.signal.aborted) {
        fail(resource, "Audio samples could not load or decode. Check your connection and retry audio.");
      }
    });
  }, [fail, release, report, silence]);

  const activate = useCallback(() => {
    const resource = current.current;
    if (!resource?.buffers) return false; // Never queue playback during loading.
    const context = resource.context;
    if (context.state === "running") { resource.activated = true; return true; }
    if (resource.activation) return resource.activation;
    report("activating", "Enabling Ear Trainer audio…");
    // Called directly from Start/audition/Resume so browsers receive the gesture.
    try {
      const resumed = context.resume();
      const timeout = new Promise((resolve, reject) => {
        resource.cancelActivation = () => reject(new Error("Activation cancelled"));
        resource.activationTimer = setTimeout(() => reject(new Error("Audio is still paused")), AUDIO_RESUME_TIMEOUT_MS);
      });
      resource.activation = Promise.race([resumed, timeout]).then(() => {
        if (current.current !== resource) return false;
        if (context.state !== "running") throw new Error("Audio is still paused");
        resource.activated = true;
        report("ready");
        return true;
      }).catch(() => {
        if (current.current === resource) report("blocked", "Your browser has not enabled sound. Select Resume audio to try again.");
        return false;
      }).finally(() => {
        clearTimeout(resource.activationTimer);
        resource.cancelActivation = null;
        resource.activation = null;
      });
      return resource.activation;
    } catch {
      report("blocked", "Your browser has not enabled sound. Select Resume audio to try again.");
      return false;
    }
  }, [report]);

  const play = useCallback(pitch => {
    const resource = current.current;
    if (!resource?.buffers || !isSupportedAudioPitch(pitch)) return false;
    const context = resource.context;
    if (context.state !== "running") {
      silence(resource);
      report("blocked", "Sound was paused by your browser. Resume audio, then start training again.");
      return false;
    }
    let index = 0;
    for (let n = 1; n < samples.length; n++) if (pitch >= samples[n][0]) index = n;
    try {
      const source = context.createBufferSource(), gain = context.createGain();
      const node = { source, gain };
      resource.sources.add(node);
      source.buffer = resource.buffers[index];
      let rate = 1;
      for (let n = 0; n < pitch - samples[index][0]; n++) rate *= 1.05946309436;
      source.playbackRate.value = rate;
      gain.gain.value = 0.5;
      source.connect(gain); gain.connect(context.destination);
      source.onended = () => {
        if (!resource.sources.delete(node)) return;
        source.disconnect(); gain.disconnect();
      };
      source.start();
      source.stop(context.currentTime + 1);
      return true;
    } catch {
      fail(resource, "Audio playback failed. Retry audio before starting training again.");
      return false;
    }
  }, [fail, report, silence]);

  useImperativeHandle(ref, () => ({ activate, play, retry: prepare }), [activate, play, prepare]);
  useEffect(() => {
    prepare();
    return () => { const resource = current.current; current.current = null; release(resource); };
  }, [prepare, release]);
  return null;
});

export default NotePlayer;
